import React, { useState, useEffect, useCallback } from 'react';
import { Navigation } from '@/components/layout/Navigation';
import { Footer } from '@/components/layout/Footer';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Store, 
  Plus, 
  QrCode, 
  MapPin, 
  Package,
  Trash2,
  Download,
  Copy,
  ExternalLink,
  Loader2,
  Search,
  ShoppingCart,
  Barcode,
  AlertTriangle,
  Crown,
  BarChart3,
  Palette
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import StoreAnalyticsDialog from '@/components/stores/StoreAnalyticsDialog';
import StoreBrandingDialog from '@/components/stores/StoreBrandingDialog';

const API = process.env.REACT_APP_BACKEND_URL;

const StoresPage = () => {
  const { user, token } = useAuth();
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [inventoryDialogOpen, setInventoryDialogOpen] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [analyticsDialogOpen, setAnalyticsDialogOpen] = useState(false);
  const [brandingDialogOpen, setBrandingDialogOpen] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [newStore, setNewStore] = useState({
    name: '',
    address: '',
    city: '',
    postcode: '',
    description: ''
  });

  const [newProduct, setNewProduct] = useState({
    product_barcode: '',
    aisle: '',
    price: ''
  });

  const isPremium = ['pro', 'enterprise', 'festival'].includes(user?.subscription_plan) || user?.role === 'admin';

  const fetchStores = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/stores`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStores(response.data.stores || []);
    } catch (error) {
      console.error('Error fetching stores:', error);
      toast.error('Failed to load stores');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (isPremium) {
      fetchStores();
    }
  }, [isPremium, fetchStores]);

  const createStore = async () => {
    if (!newStore.name || !newStore.address || !newStore.city || !newStore.postcode) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/api/stores`, newStore, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Store created successfully!');
      setCreateDialogOpen(false);
      setNewStore({ name: '', address: '', city: '', postcode: '', description: '' });
      fetchStores();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create store');
    } finally {
      setSubmitting(false);
    }
  };

  const deleteStore = async (storeId) => {
    if (!confirm('Are you sure you want to delete this store? All inventory will be removed.')) return;

    try {
      await axios.delete(`${API}/api/stores/${storeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Store deleted');
      fetchStores();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete store');
    }
  };

  const fetchInventory = async (store) => {
    setSelectedStore(store);
    setInventoryDialogOpen(true);
    setInventoryLoading(true);
    try {
      const response = await axios.get(`${API}/api/stores/${store.id}/inventory?in_stock_only=false`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInventory(response.data.inventory || []);
    } catch (error) {
      toast.error('Failed to load inventory');
    } finally {
      setInventoryLoading(false);
    }
  };

  const addToInventory = async () => {
    if (!newProduct.product_barcode) {
      toast.error('Please enter a barcode');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/api/stores/${selectedStore.id}/inventory`, {
        product_barcode: newProduct.product_barcode,
        in_stock: true,
        aisle: newProduct.aisle || null,
        price: newProduct.price ? parseFloat(newProduct.price) : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Product added to inventory!');
      setNewProduct({ product_barcode: '', aisle: '', price: '' });
      fetchInventory(selectedStore);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add product. Make sure the barcode exists in our database.');
    } finally {
      setSubmitting(false);
    }
  };

  const removeFromInventory = async (barcode) => {
    try {
      await axios.delete(`${API}/api/stores/${selectedStore.id}/inventory/${barcode}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Product removed');
      fetchInventory(selectedStore);
    } catch (error) {
      toast.error('Failed to remove product');
    }
  };

  const showQRCode = (store) => {
    setSelectedStore(store);
    setQrDialogOpen(true);
  };

  const downloadQRCode = () => {
    const canvas = document.getElementById('store-qr-canvas');
    const svg = document.getElementById('store-qr-code');
    
    // Create canvas from SVG
    const svgData = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    img.onload = () => {
      const c = document.createElement('canvas');
      c.width = 300;
      c.height = 300;
      const ctx = c.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, 300, 300);
      ctx.drawImage(img, 0, 0, 300, 300);
      
      const a = document.createElement('a');
      a.download = `${selectedStore.name.replace(/\s+/g, '-')}-qr-code.png`;
      a.href = c.toDataURL('image/png');
      a.click();
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const copyQRLink = () => {
    navigator.clipboard.writeText(selectedStore.qr_code_url);
    toast.success('Link copied to clipboard!');
  };

  const seedDemoStore = async () => {
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/api/stores/seed-demo`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchStores();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create demo store');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isPremium) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="max-w-4xl mx-auto px-4 py-12">
          <Card className="border-amber-200 bg-amber-50/50">
            <CardContent className="p-12 text-center">
              <Crown className="w-16 h-16 text-amber-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-2">Premium Feature</h2>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                The Store QR Code feature allows supermarkets to help customers quickly find products that are safe for their allergies. 
                Upgrade to Pro, Enterprise, or Festival plan to create and manage stores.
              </p>
              <Button asChild className="bg-[#5DBDB3] hover:bg-[#4AA89E]">
                <a href="/subscription">Upgrade Now</a>
              </Button>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="stores-page">
      <Navigation />
      
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-[#5DBDB3]/10 flex items-center justify-center">
              <Store className="w-6 h-6 text-[#5DBDB3]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Supermarket QR Codes</h1>
              <p className="text-muted-foreground">Help customers find allergen-safe products in your stores</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={seedDemoStore} disabled={submitting} data-testid="seed-demo-store-btn">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Package className="w-4 h-4 mr-2" />}
              Seed Demo Store
            </Button>
            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#5DBDB3] hover:bg-[#4AA89E]" data-testid="add-store-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Store
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create New Store</DialogTitle>
                  <DialogDescription>
                    Add your supermarket or store to the Seen Beyond the Label network
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Store Name *</Label>
                    <Input 
                      id="name"
                      placeholder="e.g., Tesco Express - High Street"
                      value={newStore.name}
                      onChange={(e) => setNewStore({...newStore, name: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="address">Address *</Label>
                    <Input 
                      id="address"
                      placeholder="123 High Street"
                      value={newStore.address}
                      onChange={(e) => setNewStore({...newStore, address: e.target.value})}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="city">City *</Label>
                      <Input 
                        id="city"
                        placeholder="London"
                        value={newStore.city}
                        onChange={(e) => setNewStore({...newStore, city: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="postcode">Postcode *</Label>
                      <Input 
                        id="postcode"
                        placeholder="SW1A 1AA"
                        value={newStore.postcode}
                        onChange={(e) => setNewStore({...newStore, postcode: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea 
                      id="description"
                      placeholder="Brief description of your store..."
                      value={newStore.description}
                      onChange={(e) => setNewStore({...newStore, description: e.target.value})}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
                  <Button onClick={createStore} disabled={submitting} className="bg-[#5DBDB3] hover:bg-[#4AA89E]">
                    {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                    Create Store
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* How It Works */}
        <Card className="mb-8 border-[#5DBDB3]/30 bg-gradient-to-br from-[#5DBDB3]/5 to-transparent">
          <CardContent className="p-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <QrCode className="w-5 h-5 text-[#5DBDB3]" />
              How Store QR Codes Work
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-[#5DBDB3] text-white flex items-center justify-center text-sm font-bold shrink-0">1</div>
                <div>
                  <p className="font-medium">Create Your Store</p>
                  <p className="text-sm text-muted-foreground">Add your store details and get a unique QR code</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-[#5DBDB3] text-white flex items-center justify-center text-sm font-bold shrink-0">2</div>
                <div>
                  <p className="font-medium">Add Your Inventory</p>
                  <p className="text-sm text-muted-foreground">Scan or enter barcodes of products you stock</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-[#5DBDB3] text-white flex items-center justify-center text-sm font-bold shrink-0">3</div>
                <div>
                  <p className="font-medium">Customers Scan</p>
                  <p className="text-sm text-muted-foreground">They see which products are safe for their allergies</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stores List */}
        {loading ? (
          <div className="flex items-center justify-center py-12" data-testid="stores-loading">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : stores.length === 0 ? (
          <Card data-testid="no-stores-card">
            <CardContent className="p-12 text-center">
              <Store className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Stores Yet</h3>
              <p className="text-muted-foreground mb-4">Create your first store to generate a QR code for your customers</p>
              <Button onClick={() => setCreateDialogOpen(true)} className="bg-[#5DBDB3] hover:bg-[#4AA89E]" data-testid="create-first-store-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create First Store
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-6" data-testid="stores-list">
            {stores.map((store) => (
              <Card key={store.id} className="hover:shadow-lg transition-shadow" data-testid={`store-card-${store.id}`}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg" data-testid={`store-name-${store.id}`}>{store.name}</CardTitle>
                      <CardDescription className="flex items-center gap-1 mt-1">
                        <MapPin className="w-3 h-3" />
                        {store.address}, {store.city} {store.postcode}
                      </CardDescription>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => deleteStore(store.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`delete-store-${store.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {store.description && (
                    <p className="text-sm text-muted-foreground">{store.description}</p>
                  )}
                  
                  <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <div className="flex items-center gap-2">
                      <Package className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm">Products in stock</span>
                    </div>
                    <Badge variant="secondary">{store.inventory_count || 0}</Badge>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <Button variant="outline" onClick={() => fetchInventory(store)} data-testid={`manage-inventory-${store.id}`}>
                      <ShoppingCart className="w-4 h-4 mr-2" />
                      Inventory
                    </Button>
                    <Button className="bg-[#5DBDB3] hover:bg-[#4AA89E]" onClick={() => showQRCode(store)} data-testid={`view-qr-${store.id}`}>
                      <QrCode className="w-4 h-4 mr-2" />
                      QR Code
                    </Button>
                    <Button variant="outline" onClick={() => { setSelectedStore(store); setAnalyticsDialogOpen(true); }} data-testid={`view-analytics-${store.id}`}>
                      <BarChart3 className="w-4 h-4 mr-2" />
                      Analytics
                    </Button>
                    <Button variant="outline" onClick={() => { setSelectedStore(store); setBrandingDialogOpen(true); }} data-testid={`edit-branding-${store.id}`}>
                      <Palette className="w-4 h-4 mr-2" />
                      Branding
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Inventory Dialog */}
      <Dialog open={inventoryDialogOpen} onOpenChange={setInventoryDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5" />
              {selectedStore?.name} - Inventory
            </DialogTitle>
            <DialogDescription>
              Add products by scanning their barcode or entering it manually
            </DialogDescription>
          </DialogHeader>

          {/* Add Product Form */}
          <div className="p-4 bg-muted rounded-lg space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label htmlFor="barcode" className="text-xs">Barcode *</Label>
                <Input
                  id="barcode"
                  placeholder="5000157024671"
                  value={newProduct.product_barcode}
                  onChange={(e) => setNewProduct({...newProduct, product_barcode: e.target.value})}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="aisle" className="text-xs">Aisle</Label>
                <Input
                  id="aisle"
                  placeholder="Aisle 5"
                  value={newProduct.aisle}
                  onChange={(e) => setNewProduct({...newProduct, aisle: e.target.value})}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="price" className="text-xs">Price (£)</Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  placeholder="2.50"
                  value={newProduct.price}
                  onChange={(e) => setNewProduct({...newProduct, price: e.target.value})}
                />
              </div>
            </div>
            <Button onClick={addToInventory} disabled={submitting} className="w-full bg-[#5DBDB3] hover:bg-[#4AA89E]">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Add to Inventory
            </Button>
          </div>

          {/* Inventory Table */}
          {inventoryLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : inventory.length === 0 ? (
            <div className="text-center py-8">
              <Package className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
              <p className="text-muted-foreground">No products in inventory yet</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead>Barcode</TableHead>
                  <TableHead>Aisle</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {inventory.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{item.product_name}</p>
                        <p className="text-xs text-muted-foreground">{item.brand}</p>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{item.product_barcode}</TableCell>
                    <TableCell>{item.aisle || '-'}</TableCell>
                    <TableCell>{item.price ? `£${item.price.toFixed(2)}` : '-'}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" onClick={() => removeFromInventory(item.product_barcode)} className="text-red-500 hover:text-red-700">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="w-5 h-5" />
              Store QR Code
            </DialogTitle>
            <DialogDescription>
              Print this QR code and display it in your store. Customers can scan it to see safe products.
            </DialogDescription>
          </DialogHeader>

          {selectedStore && (
            <div className="space-y-6 py-4">
              <div className="flex justify-center p-4 bg-white rounded-xl border">
                <QRCodeSVG
                  id="store-qr-code"
                  value={selectedStore.qr_code_url}
                  size={200}
                  level="H"
                  includeMargin={true}
                  fgColor="#1a1a1a"
                />
              </div>

              <div className="text-center">
                <p className="font-semibold">{selectedStore.name}</p>
                <p className="text-sm text-muted-foreground">{selectedStore.address}, {selectedStore.city}</p>
              </div>

              <div className="p-3 bg-muted rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">Scan URL</p>
                <code className="text-xs break-all">{selectedStore.qr_code_url}</code>
              </div>

              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={copyQRLink}>
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Link
                </Button>
                <Button variant="outline" className="flex-1" onClick={downloadQRCode}>
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
                <Button variant="outline" className="flex-1" onClick={() => window.open(selectedStore.qr_code_url, '_blank')}>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Preview
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Analytics Dialog */}
      <StoreAnalyticsDialog
        store={selectedStore}
        token={token}
        open={analyticsDialogOpen}
        onOpenChange={setAnalyticsDialogOpen}
      />

      {/* Branding Dialog */}
      <StoreBrandingDialog
        store={selectedStore}
        token={token}
        open={brandingDialogOpen}
        onOpenChange={setBrandingDialogOpen}
        onUpdate={(updatedStore) => {
          setStores(stores.map(s => s.id === updatedStore.id ? updatedStore : s));
        }}
      />

      <Footer />
    </div>
  );
};

export default StoresPage;
